// CSV generation for the Incomm VOID Report.
//
// Built in the browser from the same enriched rows the table renders, so the
// file always matches what the user is looking at. Every formatting decision
// is an explicit option rather than a hard-coded choice — the export modal
// drives all of it.

import { COLUMN_BY_ID, formatStamp, formatBool, formatStatus } from './columns';

export const DELIMITERS = [
  { id: 'comma',     label: 'Comma  ( , )',      short: 'Comma',     value: ',' },
  { id: 'semicolon', label: 'Semicolon  ( ; )',  short: 'Semicolon', value: ';' },
  { id: 'tab',       label: 'Tab',               short: 'Tab',       value: '\t' },
  { id: 'pipe',      label: 'Pipe  ( | )',       short: 'Pipe',      value: '|' },
];

export const DEFAULT_EXPORT_OPTIONS = {
  delimiter: 'comma',
  dateFormat: 'iso',
  boolFormat: 'yes_no',
  statusFormat: 'label',
  nullText: '',
  quoteMode: 'minimal',
  includeBom: true,          // Excel mangles non-ASCII without it
  lineEnding: 'crlf',        // what Excel expects
  forceTextTracking: true,   // stop Excel eating 12-digit FedEx numbers
  includeHeader: true,
  filenameTemplate: 'incomm-void-report_{start}_to_{end}',
};

/** The display/export value for one column of one row, before CSV quoting. */
export const cellValue = (row, columnId, options) => {
  const col = COLUMN_BY_ID[columnId];
  if (!col) return '';

  if (col.type === 'status') return formatStatus(row, options.statusFormat);

  const raw = row[columnId];

  if (raw === null || raw === undefined || raw === '') {
    // A false boolean is a real value, not a blank — only true nulls get nullText.
    return raw === null || raw === undefined ? options.nullText : '';
  }

  switch (col.type) {
    case 'datetime': return formatStamp(raw, options.dateFormat);
    case 'date':     return formatStamp(raw, options.dateFormat, { dateOnly: true });
    case 'bool':     return formatBool(raw, options.boolFormat);
    case 'number':   return String(raw);
    case 'tracking': return String(raw);
    case 'text':
    default:         return String(raw);
  }
};

const escapeCell = (value, { delimiter, quoteMode }) => {
  const s = value === null || value === undefined ? '' : String(value);
  const mustQuote = quoteMode === 'always'
    || s.includes(delimiter) || s.includes('"') || s.includes('\n') || s.includes('\r');
  return mustQuote ? `"${s.replace(/"/g, '""')}"` : s;
};

/**
 * Excel reads a bare 877233003102 as a number and shows 8.77233E+11, and it
 * drops the leading characters of some UPS numbers too. ="..." forces the
 * whole field to text. It is a formula, so it must always be quoted; the
 * escape above handles that because the value contains a double quote.
 */
const asExcelText = (value) => (value === '' ? '' : `="${String(value).replace(/"/g, '""')}"`);

/**
 * Build the CSV body.
 *
 * @param rows     enriched rows, already filtered and sorted as the user wants
 * @param columns  ordered [{ id, header }] — header is the user's own label
 * @param options  DEFAULT_EXPORT_OPTIONS shape
 */
export const buildCsv = (rows, columns, options) => {
  const opts = { ...DEFAULT_EXPORT_OPTIONS, ...options };
  const delimiter = (DELIMITERS.find((d) => d.id === opts.delimiter) || DELIMITERS[0]).value;
  const eol = opts.lineEnding === 'lf' ? '\n' : '\r\n';
  const esc = (v) => escapeCell(v, { delimiter, quoteMode: opts.quoteMode });

  const lines = [];

  if (opts.includeHeader) {
    lines.push(columns.map((c) => esc(c.header ?? COLUMN_BY_ID[c.id]?.label ?? c.id)).join(delimiter));
  }

  for (const row of rows) {
    const cells = columns.map((c) => {
      let v = cellValue(row, c.id, opts);
      if (opts.forceTextTracking && COLUMN_BY_ID[c.id]?.type === 'tracking') {
        v = asExcelText(v);
      }
      return esc(v);
    });
    lines.push(cells.join(delimiter));
  }

  return (opts.includeBom ? '﻿' : '') + lines.join(eol) + eol;
};

export const buildFilename = (template, { startDate, endDate }) => {
  const safe = (template || DEFAULT_EXPORT_OPTIONS.filenameTemplate)
    .replace(/\{start\}/g, startDate)
    .replace(/\{end\}/g, endDate)
    .replace(/\{today\}/g, new Date().toLocaleDateString('en-CA'))
    .replace(/[\\/:*?"<>|]/g, '-')
    .trim();
  return `${safe || 'incomm-void-report'}.csv`;
};

/** Hand the built CSV to the browser as a download. */
export const downloadCsv = (csvText, filename) => {
  // text/csv;charset=utf-8 plus the BOM inside the text is what makes Excel
  // open it as UTF-8 on Windows.
  const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 10000);
};
